import React, { useEffect, useRef, useState } from "react";

import { SUBTITLES_BACKGROUND_OPTIONS } from "../constants";
import { SUBTITLES_BACKGROUND } from "../enums";
import {
  getAllFromStorage,
  PersistedStateName,
  saveToStorage,
  storageKey,
  version
} from "../helpers/persist";
import { useGeolocation } from "../hooks/useGeolocation/useGeolocation";
import { useLoginData } from "../hooks/useLoginData/useLoginData";
import { PlayerEmbedParams } from "../types/common";

import { createSelectors } from "./createSelectors";
import { getInitialState } from "./parseStoreValues";
import {
  createPlayerStore,
  PlayerStoreContext,
  PlayerStoreProperties,
  PlayerStoreWithSelectors
} from "./store";

const removeInvalidPersistedValues = () => {
  const persistedValues = getAllFromStorage();

  const subtitlesBackground =
    persistedValues[PersistedStateName.SUBTITLES_BACKGROUND];
  if (
    subtitlesBackground &&
    !SUBTITLES_BACKGROUND_OPTIONS.includes(
      subtitlesBackground as SUBTITLES_BACKGROUND
    )
  ) {
    saveToStorage(PersistedStateName.SUBTITLES_BACKGROUND, undefined);
  }
};

const removeOutdatedPersistedValues = () => {
  try {
    const playerUISettings = JSON.parse(localStorage.getItem(storageKey) || "");
    if (playerUISettings?.$version !== version) {
      localStorage.removeItem(storageKey);
    }
  } catch (e) {
    /* empty */
  }
};

export let store: PlayerStoreWithSelectors;

export const PlayerStoreProvider = ({
  children,
  embedParams,
  initialState = {}
}: React.PropsWithChildren<{
  embedParams: PlayerEmbedParams;
  initialState?: Partial<PlayerStoreProperties>;
}>) => {
  const storeRef = useRef<PlayerStoreWithSelectors>();
  const [storeReady, setStoreReady] = useState(false);

  const loginData = useLoginData({
    requireLogin: !embedParams.backgroundVideo
  });
  const geolocation = useGeolocation();

  useEffect(() => {
    if (!storeRef.current && loginData && geolocation) {
      store = createSelectors(createPlayerStore(embedParams.id));
      storeRef.current = store;

      // Initialize the store
      const state = storeRef.current?.getState();
      if (!state) {
        throw new Error("Store does not exists!");
      }

      removeOutdatedPersistedValues();
      removeInvalidPersistedValues();

      storeRef.current?.setState(
        getInitialState(embedParams, {
          geolocation,
          loginData,
          ...initialState
        }),
        false,
        "setInitialState"
      );
      setStoreReady(true);
    }
  }, [embedParams, geolocation, initialState, loginData]);

  if (storeReady && storeRef.current) {
    return (
      <PlayerStoreContext.Provider value={storeRef.current}>
        {children}
      </PlayerStoreContext.Provider>
    );
  }

  return null;
};
